import { Box, List, sprinkles, Text } from "@saleor/macaw-ui-next";
import { useState } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";

import { type SidebarMenuItem } from "./types";
import { isMenuActive } from "./utils";

interface Props {
  menuItem: SidebarMenuItem;
}

// Light-tint active treatment: soft indigo background with indigo accent
// text/icon, no heavy fill or outer glow.
const ACTIVE_BG = "#EEF0FF";
const ACTIVE_FG = "#4F46E5";
const HOVER_BG = "#F5F7FF";
const HOVER_FG = "#4F46E5";

export const SingleItem = ({ menuItem }: Props) => {
  const { pathname } = useLocation();
  const active = isMenuActive(pathname, menuItem);
  const [hovered, setHovered] = useState(false);
  const handleMenuItemClick = () => {
    if (menuItem.onClick) {
      menuItem.onClick();
    }
  };

  const baseStyle: React.CSSProperties = { borderRadius: 8 };
  const containerStyle: React.CSSProperties = active
    ? { ...baseStyle, backgroundColor: ACTIVE_BG }
    : hovered
      ? { ...baseStyle, backgroundColor: HOVER_BG }
      : baseStyle;

  const textColor = active ? ACTIVE_FG : hovered ? HOVER_FG : "#374151";
  const iconColor = active
    ? ACTIVE_FG
    : hovered
      ? HOVER_FG
      : (menuItem.iconColor ?? "#6B7280");

  return (
    <List.Item
      borderRadius={3}
      paddingX={0}
      active={false}
      onClick={handleMenuItemClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-test-id={`menu-item-label-${menuItem.id}`}
      position="relative"
      style={containerStyle}
    >
      <Link
        to={menuItem.url || ""}
        replace={active}
        className={sprinkles({ display: "block", width: "100%" })}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={3}
          paddingY={2.5}
          paddingX={3}
        >
          {menuItem.icon && (
            <Box
              display="flex"
              alignItems="center"
              flexShrink="0"
              style={{ color: iconColor }}
            >
              {menuItem.icon}
            </Box>
          )}
          <Text
            size={3}
            fontWeight={active ? "bold" : "medium"}
            style={{ color: textColor }}
          >
            {menuItem.label}
          </Text>
        </Box>
      </Link>
      {menuItem.endAdornment && (
        <Box position="absolute" right={2} zIndex={"3"}>
          {menuItem.endAdornment}
        </Box>
      )}
    </List.Item>
  );
};
